import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, Cell } from "recharts";
import { ChevronRight, FileText, Mail, Phone, Pencil, Check, X, Home, Building2, Factory, Landmark, Lock } from "lucide-react";
import { toast } from "sonner";
import ReportModal from "@/components/ReportModal";
import ServiceTickets from "@/components/ServiceTickets";
import DocumentVault from "@/components/DocumentVault";
import UptimeRing from "@/components/UptimeRing";
import TechnicalGuide from "@/components/TechnicalGuide";
import EnergyFlowGraph from "@/components/EnergyFlowGraph";
import PowerProfileChart from "@/components/PowerProfileChart";
import UtilizationDonuts from "@/components/UtilizationDonuts";
import VoltageStabilityChart from "@/components/VoltageStabilityChart";

export default function PlantDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [plant, setPlant] = useState(null);
    const [tab, setTab] = useState("day");
    const [reportOpen, setReportOpen] = useState(false);
    const [hover, setHover] = useState(null);
    // Live energy flow (multi-stream telemetry + auto-detected system type)
    const [energyFlow, setEnergyFlow] = useState(null);
    // Grid stability — last 24h AC/grid voltage + frequency samples
    const [voltage, setVoltage] = useState(null);
    // Date picker for power chart — defaults to today (IST) and can be scrolled backwards
    const todayIst = () => {
        const now = new Date();
        // Convert to IST by adding offset (IST = UTC+5:30). Format YYYY-MM-DD in IST.
        const istMs = now.getTime() + (5.5 * 60 * 60 * 1000) + (now.getTimezoneOffset() * 60 * 1000);
        const d = new Date(istMs);
        return d.toISOString().slice(0, 10);
    };
    const [chartDate, setChartDate] = useState(todayIst());
    // Custom Range tab state — start/end date pickers + fetched series.
    // Default window: last 14 days ending today. Applies to Admin,
    // Sales *and* Customer views (Universal Chart Access).
    const _daysAgoIso = (n) => {
        const now = new Date();
        const istMs = now.getTime() + (5.5 * 60 * 60 * 1000) + (now.getTimezoneOffset() * 60 * 1000);
        const d = new Date(istMs); d.setUTCDate(d.getUTCDate() - n);
        return d.toISOString().slice(0, 10);
    };
    const [rangeStart, setRangeStart] = useState(_daysAgoIso(13));
    const [rangeEnd, setRangeEnd] = useState(todayIst());
    const [rangeSeries, setRangeSeries] = useState(null);
    const [rangeLoading, setRangeLoading] = useState(false);
    const canEditContact = user && (user.role === "super_admin" || user.role === "sales");

    useEffect(() => {
        const url = chartDate ? `/plants/${id}?date=${chartDate}` : `/plants/${id}`;
        api.get(url).then(r => setPlant(r.data));
    }, [id, chartDate]);

    // Fetch live energy-flow snapshot (system type, flow, multi-stream profile,
    // utilization aggregates). Refreshed every 60s alongside the plant poll.
    useEffect(() => {
        let cancelled = false;
        const load = () => api.get(`/plants/${id}/energy-flow`)
            .then(r => { if (!cancelled) setEnergyFlow(r.data); })
            .catch(() => { /* silent — endpoint may 404 on legacy plants */ });
        load();
        const t = setInterval(load, 60000);
        return () => { cancelled = true; clearInterval(t); };
    }, [id]);

    // Fetch the last-24h voltage / grid-frequency series for the stability
    // chart. Poll every 5 min — voltage moves slowly and the SolarOS API
    // endpoint is rate-sensitive.
    useEffect(() => {
        let cancelled = false;
        const load = () => api.get(`/plants/${id}/voltage-history?hours=24`)
            .then(r => { if (!cancelled) setVoltage(r.data); })
            .catch(() => { /* silent — inverter may not expose voltage measure-points */ });
        load();
        const t = setInterval(load, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(t); };
    }, [id]);

    // Real-time refresh — every 60 s poll the plant endpoint so the "Today"
    // bar on the 30-day chart and live-power KPI stay current. Runs only when
    // the user is viewing today (historical dates are static).
    useEffect(() => {
        const today = todayIst();
        if (chartDate !== today) return;
        const t = setInterval(() => {
            api.get(`/plants/${id}?date=${chartDate}`).then(r => setPlant(r.data)).catch(() => { });
        }, 60000);
        return () => clearInterval(t);
    }, [id, chartDate]);

    // Fetch the custom-range series when the tab is active and the
    // dates form a valid window. Backend caps the range at 366 days so
    // the SolarOS chunked fetch stays bounded.
    useEffect(() => {
        if (tab !== "custom") return;
        if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) return;
        let cancelled = false;
        setRangeLoading(true);
        api.get(`/plants/${id}/daily-history?start_date=${rangeStart}&end_date=${rangeEnd}`)
            .then(r => { if (!cancelled) setRangeSeries(r.data); })
            .catch(() => { if (!cancelled) setRangeSeries(null); })
            .finally(() => { if (!cancelled) setRangeLoading(false); });
        return () => { cancelled = true; };
    }, [tab, id, rangeStart, rangeEnd]);

    if (!plant) return <div className="text-zinc-500 text-sm">Loading plant…</div>;

    const powerData = plant.power_history.map(d => {
        const ts = typeof d.timestamp === "number" ? d.timestamp * 1000 : d.timestamp;
        return {
            time: new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }),
            kW: d.power_kw || 0,
        };
    });
    const dailyData = plant.daily_history.map(d => ({
        date: d.date.slice(5),
        kWh: d.generation_kwh,
        isToday: !!d.is_today,
    }));
    // Custom-range series shaped identically to `dailyData` so we can reuse the BarChart.
    const rangeData = (rangeSeries?.daily || []).map(d => ({
        date: d.date.slice(5),
        kWh: d.generation_kwh,
        isToday: d.date === todayIst(),
    }));
    const rangeTotal = rangeSeries?.total_kwh ?? 0;

    // Build 3-hour interval ticks (00:00, 03:00, ... 21:00) that exist in the data
    const desiredHours = new Set(["00", "03", "06", "09", "12", "15", "18", "21"]);
    const powerTicks = [...new Set(
        powerData.filter(d => desiredHours.has(d.time.slice(0, 2)) && d.time.slice(3, 5) === "00").map(d => d.time)
    )];

    const inverters = plant.inverters || plant.devices || [];
    const dataLoggers = plant.data_loggers || [];
    // SolarOS "cleared" alerts carry `alertEndTime > 0`; active alerts have
    // `alertEndTime == 0` (or absent). We use that as the source of truth
    // instead of the legacy `status === 1` heuristic which SolarOS never sets.
    const activeAlerts = plant.alerts.filter(a => !a.alertEndTime);

    return (
        <div className="space-y-6" data-testid="plant-detail-page">
            <div className="flex items-center gap-2 text-xs text-zinc-500">
                <Link to="/plants" className="hover:text-[#0A0A0A]">Plants</Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-zinc-700 font-mono">{plant.id}</span>
            </div>

            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900">{plant.customer_name || plant.name}</h1>
                        <StatusBadge health={plant.health} />
                        {typeof plant.warranty_days_remaining === "number" && (() => {
                            const d = plant.warranty_days_remaining;
                            const active = plant.warranty_active;
                            const tone = !active ? { c: "#B91C1C", bg: "#FEE2E2", b: "#FCA5A5" } : d < 60 ? { c: "#B91C1C", bg: "#FEE2E2", b: "#FCA5A5" } : d < 365 ? { c: "#B45309", bg: "#FEF3C7", b: "#FCD34D" } : { c: "#166534", bg: "#DCFCE7", b: "#86EFAC" };
                            const yrs = Math.floor(Math.abs(d) / 365);
                            const mo = Math.floor((Math.abs(d) % 365) / 30);
                            return (
                                <span className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded-full" style={{ background: tone.bg, color: tone.c, borderWidth: 1, borderStyle: "solid", borderColor: tone.b }} title={`Commissioned ${plant.commissioned_at} · Warranty ${active ? "ends" : "ended"} ${plant.warranty_end}`} data-testid="warranty-badge">
                                    {active ? "🛡" : "⚠"} {active ? `${yrs}y ${mo}m warranty left` : `Warranty expired ${yrs}y ${mo}m ago`}
                                </span>
                            );
                        })()}
                        {plant.category && (
                            <CategoryBadge category={plant.category} />
                        )}
                    </div>
                    <div className="text-sm text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                        <span>{plant.address}</span>
                        {plant.customer_name && (
                            <>
                                <span className="text-zinc-300">·</span>
                                <span className="font-mono text-[11px] text-zinc-400">{plant.name}</span>
                            </>
                        )}
                    </div>
                </div>
                <Button onClick={() => setReportOpen(true)} className="bg-[#0A0A0A] hover:bg-black text-white rounded-full h-10" data-testid="generate-report-btn">
                    <FileText className="w-4 h-4 mr-2" /> Generate Report
                </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Kpi label="Capacity" value={`${plant.installed_capacity_kw}`} unit="kWp" />
                <Kpi label="Live Power" value={plant.current_power_kw?.toFixed(1)} unit="kW" accent="orange" />
                <Kpi label="Expected" value={plant.expected_power_kw?.toFixed(1)} unit="kW" accent="green" />
                <Kpi label="Inverters" value={inverters.length} unit={`× ${dataLoggers.length} logger${dataLoggers.length !== 1 ? "s" : ""}`} />
            </div>

            <UptimeRing uptime={plant.uptime} testid="plant-uptime-ring" />

            {/* Live energy flow — auto-adapts hybrid vs on-grid */}
            {energyFlow && (
                <EnergyFlowGraph data={energyFlow} />
            )}

            <ContactCard plant={plant} canEdit={canEditContact} onSaved={(updated) => setPlant(p => ({ ...p, ...updated }))} />

            <CategoryCard plant={plant} canEdit={canEditContact} onSaved={(updated) => setPlant(p => ({ ...p, ...updated }))} />

            {/* Multi-stream power profile — hybrid systems only. On-grid
                plants have a single PV stream that the 30-day generation
                chart already covers, so the additional profile view adds
                clutter without new insight. */}
            {energyFlow?.system_type === "hybrid" && energyFlow?.power_profile?.length > 0 && (
                <PowerProfileChart data={energyFlow.power_profile} isHybrid />
            )}

            {/* Utilization donuts — hybrid systems only (on-grid plants
                don't produce meaningful Charge/Discharge splits) */}
            {energyFlow?.system_type === "hybrid" && energyFlow?.utilization && (
                <UtilizationDonuts utilization={energyFlow.utilization} isHybrid />
            )}

            {/* Grid Stability — last 24h AC / grid voltage + frequency so
                service engineers can spot brownouts, sags and swells at a
                glance. Rendered only when the inverter reports the required
                measure-points (i.e. `sample_count > 0`). */}
            {voltage && voltage.sample_count > 0 && (
                <VoltageStabilityChart data={voltage} />
            )}

            <div className="bg-white border border-zinc-200 rounded-sm p-5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Production</div>
                        <div className="text-sm text-zinc-700 mt-0.5">
                            {tab === "day"
                                ? `Power on ${chartDate}${chartDate === todayIst() ? " (Today)" : ""} · 5-min interval`
                                : tab === "custom"
                                    ? (rangeStart && rangeEnd && rangeStart <= rangeEnd
                                        ? `${rangeStart} → ${rangeEnd} · daily · Total ${rangeTotal.toFixed(1)} kWh`
                                        : "Select a valid start / end date")
                                    : (
                                        <span className="inline-flex items-center gap-2">
                                            Last 30 days · daily
                                            <span className="inline-flex items-center gap-1 text-[10px] font-mono text-[#16A34A] px-1.5 py-0.5 rounded-sm bg-green-50 border border-green-200" data-testid="realtime-badge">
                                                <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] animate-pulse" />
                                                LIVE · Today {plant.today_realtime_kwh?.toFixed(1) ?? "0.0"} kWh
                                            </span>
                                        </span>
                                    )}
                        </div>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                        {tab === "day" && (
                            <div className="flex items-center gap-2" data-testid="chart-date-picker-wrap">
                                <button
                                    onClick={() => {
                                        const d = new Date(chartDate + "T00:00:00Z");
                                        d.setUTCDate(d.getUTCDate() - 1);
                                        setChartDate(d.toISOString().slice(0, 10));
                                    }}
                                    className="w-7 h-7 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-50 text-zinc-600"
                                    data-testid="chart-date-prev"
                                    title="Previous day"
                                >‹</button>
                                <Input
                                    type="date"
                                    value={chartDate}
                                    max={todayIst()}
                                    onChange={e => setChartDate(e.target.value)}
                                    className="h-8 w-[150px] rounded-sm text-xs font-mono"
                                    data-testid="chart-date-picker"
                                />
                                <button
                                    onClick={() => {
                                        if (chartDate === todayIst()) return;
                                        const d = new Date(chartDate + "T00:00:00Z");
                                        d.setUTCDate(d.getUTCDate() + 1);
                                        const next = d.toISOString().slice(0, 10);
                                        setChartDate(next > todayIst() ? todayIst() : next);
                                    }}
                                    disabled={chartDate === todayIst()}
                                    className="w-7 h-7 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-50 text-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="chart-date-next"
                                    title="Next day"
                                >›</button>
                                {chartDate !== todayIst() && (
                                    <button
                                        onClick={() => setChartDate(todayIst())}
                                        className="text-xs font-medium text-[#0A0A0A] hover:text-[#000000] px-2"
                                        data-testid="chart-date-today"
                                    >Today</button>
                                )}
                            </div>
                        )}
                        {tab === "custom" && (
                            <div className="flex items-center gap-2 flex-wrap" data-testid="chart-range-picker-wrap">
                                <Input
                                    type="date"
                                    value={rangeStart}
                                    max={rangeEnd || todayIst()}
                                    onChange={e => setRangeStart(e.target.value)}
                                    className="h-8 w-[150px] rounded-sm text-xs font-mono"
                                    data-testid="chart-range-start"
                                />
                                <span className="text-xs text-zinc-500">→</span>
                                <Input
                                    type="date"
                                    value={rangeEnd}
                                    min={rangeStart}
                                    max={todayIst()}
                                    onChange={e => setRangeEnd(e.target.value)}
                                    className="h-8 w-[150px] rounded-sm text-xs font-mono"
                                    data-testid="chart-range-end"
                                />
                            </div>
                        )}
                        {/* Live-updating hover readout */}
                        <div className="text-right" data-testid="chart-hover-readout">
                            <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
                                {hover ? (tab === "day" ? hover.time : hover.date) : (tab === "day" ? "Latest" : tab === "custom" ? "Range peak" : "30-day peak")}
                            </div>
                            <div className={`text-2xl font-mono font-medium leading-tight ${tab === "day" ? "text-[#16A34A]" : "text-[#0A0A0A]"}`}>
                                {tab === "day"
                                    ? (hover ? hover.kW : (powerData[powerData.length - 1]?.kW || 0)).toFixed(1)
                                    : tab === "custom"
                                        ? (hover ? hover.kWh : Math.max(0, ...rangeData.map(d => d.kWh), 0)).toFixed(1)
                                        : (hover ? hover.kWh : Math.max(0, ...dailyData.map(d => d.kWh))).toFixed(1)}
                                <span className="text-xs text-zinc-500 ml-1">{tab === "day" ? "kW" : "kWh"}</span>
                            </div>
                        </div>
                        <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden">
                            {["day", "month", "custom"].map(t => (
                                <button key={t} onClick={() => { setTab(t); setHover(null); }} data-testid={`tab-${t}`} className={`px-3 py-1.5 text-xs uppercase tracking-wider font-medium transition-colors duration-200 ${tab === t ? "bg-[#0B0D11] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
                                    {t === "day" ? "Day" : t === "month" ? "30 days" : "Custom"}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="h-72 relative" data-testid="plant-chart">
                    {tab === "day" && powerData.length === 0 && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10" data-testid="chart-empty">
                            <div className="text-center">
                                <div className="text-sm font-medium text-zinc-500">No production data for {chartDate}</div>
                                <div className="text-xs text-zinc-400 mt-1">The inverter may have been offline or the day is in the future.</div>
                            </div>
                        </div>
                    )}
                    {tab === "custom" && !rangeLoading && rangeData.length === 0 && rangeStart && rangeEnd && rangeStart <= rangeEnd && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10" data-testid="chart-range-empty">
                            <div className="text-center">
                                <div className="text-sm font-medium text-zinc-500">No production recorded in this range</div>
                                <div className="text-xs text-zinc-400 mt-1">{rangeStart} → {rangeEnd}</div>
                            </div>
                        </div>
                    )}
                    {tab === "custom" && rangeLoading && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 text-xs text-zinc-500" data-testid="chart-range-loading">
                            Loading…
                        </div>
                    )}
                    <ResponsiveContainer width="100%" height="100%">
                        {tab === "day" ? (
                            <AreaChart data={powerData}
                                onMouseMove={(s) => s?.activePayload?.[0] && setHover(s.activePayload[0].payload)}
                                onMouseLeave={() => setHover(null)}
                            >
                                <defs>
                                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#16A34A" stopOpacity={0.5} />
                                        <stop offset="100%" stopColor="#16A34A" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                                <XAxis
                                    dataKey="time"
                                    ticks={powerTicks}
                                    interval={0}
                                    tick={{ fontSize: 11, fill: "#71717A", fontFamily: 'IBM Plex Mono' }}
                                />
                                <YAxis
                                    domain={[0, 'auto']}
                                    tickFormatter={(v) => v.toFixed(1)}
                                    tick={{ fontSize: 11, fill: "#71717A", fontFamily: 'IBM Plex Mono' }}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: 4, border: "1px solid #E4E4E7", fontSize: 12, fontFamily: "Outfit" }}
                                    labelStyle={{ color: "#6B7280", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }}
                                    formatter={(v) => [`${v.toFixed(2)} kW`, "Power"]}
                                    cursor={{ stroke: "#0A0A0A", strokeWidth: 1, strokeDasharray: "3 3" }}
                                />
                                <Area type="monotone" dataKey="kW" stroke="#16A34A" strokeWidth={2} fill="url(#g1)" activeDot={{ r: 5, fill: "#0A0A0A", stroke: "#fff", strokeWidth: 2 }} />
                            </AreaChart>
                        ) : (
                            <BarChart data={tab === "custom" ? rangeData : dailyData}
                                onMouseMove={(s) => s?.activePayload?.[0] && setHover(s.activePayload[0].payload)}
                                onMouseLeave={() => setHover(null)}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#71717A", fontFamily: 'IBM Plex Mono' }} />
                                <YAxis tick={{ fontSize: 11, fill: "#71717A", fontFamily: 'IBM Plex Mono' }} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 4, border: "1px solid #E4E4E7", fontSize: 12, fontFamily: "Outfit" }}
                                    labelStyle={{ color: "#6B7280", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }}
                                    formatter={(v) => [`${v.toFixed(1)} kWh`, "Generation"]}
                                    cursor={{ fill: "rgba(249,115,22,0.06)" }}
                                />
                                <Bar dataKey="kWh" radius={[2, 2, 0, 0]}>
                                    {(tab === "custom" ? rangeData : dailyData).map((entry) => (
                                        <Cell key={entry.date} fill={entry.isToday ? "#16A34A" : "#0A0A0A"} />
                                    ))}
                                </Bar>
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-zinc-200 rounded-sm">
                    <div className="p-4 border-b border-zinc-200">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Inverter Fleet</div>
                        <div className="text-sm text-zinc-700 mt-0.5">{inverters.length} inverter{inverters.length !== 1 ? "s" : ""} registered</div>
                    </div>
                    <table className="w-full text-sm" data-testid="inverter-table">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b border-zinc-200">
                                <th className="py-2 px-4 text-left font-semibold">Model</th>
                                <th className="py-2 px-4 text-left font-semibold">Serial</th>
                                <th className="py-2 px-4 text-left font-semibold">Rated</th>
                                <th className="py-2 px-4 text-left font-semibold">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {inverters.map((d, i) => {
                                const hasActiveAlert = activeAlerts.some(a => a.deviceSn === d.serial_number);
                                let statusEl;
                                if (hasActiveAlert) {
                                    statusEl = <span className="text-xs font-mono text-[#DC2626]">▲ ALERT</span>;
                                } else if (d.state === 1) {
                                    statusEl = <span className="text-xs font-mono text-[#16A34A]">● ONLINE</span>;
                                } else {
                                    statusEl = <span className="text-xs font-mono text-zinc-500" title="Device not reporting recent telemetry — no active fault">○ OFFLINE</span>;
                                }
                                return (
                                <tr key={d.serial_number} className="border-b border-zinc-100" data-testid={`inverter-row-${i}`}>
                                    <td className="py-2.5 px-4 text-zinc-800">{d.model}</td>
                                    <td className="py-2.5 px-4 font-mono text-zinc-700 text-xs">{d.serial_number}</td>
                                    <td className="py-2.5 px-4 font-mono text-zinc-700">{d.rated_kw ? `${d.rated_kw.toFixed(1)} kW` : "—"}</td>
                                    <td className="py-2.5 px-4">{statusEl}</td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {dataLoggers.length > 0 && (
                        <div className="border-t-2 border-dashed border-zinc-200 bg-zinc-50/60 p-4" data-testid="data-loggers-panel">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Data Logger{dataLoggers.length !== 1 ? "s" : ""} · sub-component</span>
                                <span className="text-[10px] font-mono text-zinc-400">transmits telemetry — not an inverter</span>
                            </div>
                            <div className="space-y-1.5">
                                {dataLoggers.map((l, i) => (
                                    <div key={l.serial_number} className="flex items-center justify-between text-xs" data-testid={`logger-row-${i}`}>
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${l.state === 1 ? "bg-[#16A34A]" : "bg-zinc-400"}`} title={l.state === 1 ? "Reporting" : "Not reporting"} />
                                            <span className="text-zinc-800 truncate">{l.model}</span>
                                        </div>
                                        <span className="font-mono text-zinc-500 ml-2">{l.serial_number}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white border border-zinc-200 rounded-sm">
                    <div className="p-4 border-b border-zinc-200">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Active Alerts ({activeAlerts.length})</div>
                        <div className="text-sm text-zinc-700 mt-0.5">Recent error codes</div>
                    </div>
                    <div className="max-h-96 overflow-y-auto divide-y divide-zinc-100">
                        {activeAlerts.length === 0 ? (
                            <div className="p-6 text-sm text-zinc-500">No active alerts. Plant running normally.</div>
                        ) : activeAlerts.map((a, i) => (
                            <div
                                key={a.alertId}
                                className="p-4 hover:bg-zinc-50 group transition-colors duration-200"
                                data-testid={`alert-row-${i}`}
                            >
                                <div className="flex items-center gap-2 mb-1 cursor-pointer" onClick={() => nav(`/alerts/${a.alertId}`)}>
                                    <span className="text-xs font-mono text-red-600 border border-red-500/30 bg-red-500/10 rounded-sm px-1.5 py-0.5">{a.protocolName}</span>
                                    <span className="text-sm font-medium text-zinc-900 group-hover:text-[#0A0A0A] transition-colors">{a.alertName}</span>
                                    <ChevronRight className="w-3 h-3 text-zinc-400 ml-auto group-hover:text-[#0A0A0A] transition-colors" />
                                </div>
                                <div className="text-xs text-zinc-500">{a.description}</div>
                                <div className="text-[10px] font-mono text-zinc-400 mt-1">
                                    {new Date(a.alertStartTime * 1000).toLocaleString()} · SN {a.deviceSn}
                                </div>
                                {/* Manual-based technical guide surfaced inline so the team sees the fix immediately */}
                                <TechnicalGuide guide={a.technical_guide} compact testid={`plant-alert-guide-${i}`} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <ServiceTickets stationId={plant.id} />

            <DocumentVault stationId={plant.id} />

            {reportOpen && <ReportModal stationId={plant.id} ownerEmail={plant.owner_email} plantName={plant.customer_name || plant.name} onClose={() => setReportOpen(false)} />}
        </div>
    );
}

function Kpi({ label, value, unit, accent = "black" }) {
    const c = { orange: "text-[#0A0A0A]", green: "text-[#16A34A]", black: "text-zinc-900" }[accent];
    return (
        <div className="bg-white border border-zinc-200 rounded-sm p-4">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
            <div className="mt-2 flex items-baseline gap-1.5">
                <span className={`text-2xl font-medium font-mono ${c}`}>{value}</span>
                <span className="text-xs font-mono text-zinc-500">{unit}</span>
            </div>
        </div>
    );
}

function ContactCard({ plant, canEdit, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [email, setEmail] = useState(plant.owner_email || "");
    const [phone, setPhone] = useState(plant.contact_phone || "");
    const [saving, setSaving] = useState(false);

    const customerLocked = !!plant.contact_updated_by_customer;
    const canEditEffective = canEdit && !customerLocked;
    const hasChanges = (email || "") !== (plant.owner_email || "") || (phone || "") !== (plant.contact_phone || "");
    const emailOk = !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const cancel = () => {
        setEmail(plant.owner_email || "");
        setPhone(plant.contact_phone || "");
        setEditing(false);
    };

    const save = async () => {
        if (!emailOk) return;
        setSaving(true);
        try {
            const { data } = await api.patch(`/plants/${plant.id}/contact`, {
                owner_email: email || null,
                contact_phone: phone || null,
            });
            onSaved({
                owner_email: data.owner_email,
                contact_phone: data.contact_phone,
                contact_updated_by: data.contact_updated_by,
                contact_updated_at: data.contact_updated_at,
            });
            toast.success("Contact details saved");
            setEditing(false);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const formatTs = (iso) => {
        if (!iso) return "";
        try {
            const d = new Date(iso);
            return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) + " IST";
        } catch { return iso; }
    };

    return (
        <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="contact-card">
            <div className="flex items-start justify-between mb-4 gap-3">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-2">
                        Owner Contact
                        {customerLocked && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-green-50 text-green-700 border border-green-200 normal-case" data-testid="contact-locked-badge">
                                <Lock className="w-2.5 h-2.5" /> CUSTOMER-MANAGED
                            </span>
                        )}
                    </div>
                    <div className="text-sm text-zinc-700 mt-0.5">
                        {customerLocked
                            ? "Customer has set their own details from the portal — admin edits are locked."
                            : "Editable by Sales & Super Admin. All edits are logged."}
                    </div>
                </div>
                {canEditEffective && !editing && (
                    <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="rounded-sm h-8" data-testid="edit-contact-btn">
                        <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                    </Button>
                )}
                {canEditEffective && editing && (
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={cancel} className="rounded-sm h-8 text-zinc-500" data-testid="cancel-contact-btn">
                            <X className="w-3.5 h-3.5 mr-1" /> Cancel
                        </Button>
                        <Button size="sm" onClick={save} disabled={saving || !emailOk || !hasChanges} className="bg-[#16A34A] hover:bg-[#128F3F] text-white rounded-sm h-8" data-testid="save-contact-btn">
                            <Check className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving…" : "Save"}
                        </Button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block flex items-center gap-1.5">
                        <Phone className="w-3 h-3" /> Phone Number
                    </label>
                    {editing ? (
                        <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 98xxx xxxxx" className="rounded-sm font-mono" data-testid="contact-phone-input" />
                    ) : (
                        <div className="text-sm font-mono text-zinc-900 py-2 px-3 bg-zinc-50 rounded-sm border border-transparent min-h-[38px] flex items-center" data-testid="contact-phone-display">
                            {plant.contact_phone || <span className="text-zinc-400 italic">Not set</span>}
                        </div>
                    )}
                </div>
                <div>
                    <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block flex items-center gap-1.5">
                        <Mail className="w-3 h-3" /> Email
                    </label>
                    {editing ? (
                        <>
                            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="owner@example.com" className="rounded-sm font-mono" data-testid="contact-email-input" />
                            {email && !emailOk && <div className="text-[11px] text-red-600 mt-1 font-mono">Invalid email format</div>}
                        </>
                    ) : (
                        <div className="text-sm font-mono text-zinc-900 py-2 px-3 bg-zinc-50 rounded-sm border border-transparent min-h-[38px] flex items-center" data-testid="contact-email-display">
                            {plant.owner_email || <span className="text-zinc-400 italic">Not set</span>}
                        </div>
                    )}
                </div>
            </div>

            {plant.contact_updated_by && (
                <div className="mt-3 pt-3 border-t border-zinc-100 text-[11px] font-mono text-zinc-500 flex items-center gap-2 flex-wrap" data-testid="contact-audit-line">
                    <span className="text-zinc-400">Last edited by</span>
                    <span className="text-zinc-700">{plant.contact_updated_by}</span>
                    <span className="text-zinc-300">·</span>
                    <span>{formatTs(plant.contact_updated_at)}</span>
                </div>
            )}
        </div>
    );
}



const CATEGORY_META = {
    residential: { label: "Residential", icon: Home, tone: { bg: "#DBEAFE", c: "#1D4ED8", b: "#93C5FD" } },
    commercial: { label: "Commercial", icon: Building2, tone: { bg: "#FEF3C7", c: "#B45309", b: "#FCD34D" } },
    industrial: { label: "Industrial", icon: Factory, tone: { bg: "#E4E4E7", c: "#3F3F46", b: "#A1A1AA" } },
    government: { label: "Govt", icon: Landmark, tone: { bg: "#DCFCE7", c: "#166534", b: "#86EFAC" } },
};

function CategoryBadge({ category }) {
    const meta = CATEGORY_META[category];
    if (!meta) return null;
    const Icon = meta.icon;
    return (
        <span
            className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded-full"
            style={{ background: meta.tone.bg, color: meta.tone.c, borderWidth: 1, borderStyle: "solid", borderColor: meta.tone.b }}
            data-testid="category-badge"
        >
            <Icon className="w-3 h-3" /> {meta.label}
        </span>
    );
}

function CategoryCard({ plant, canEdit, onSaved }) {
    const [saving, setSaving] = useState(false);

    const set = async (cat) => {
        if (cat === plant.category || saving) return;
        setSaving(true);
        try {
            const { data } = await api.patch(`/plants/${plant.id}/category`, { category: cat });
            onSaved({
                category: data.category,
                category_updated_by: data.category_updated_by,
                category_updated_at: data.category_updated_at,
            });
            toast.success(`Category set to ${data.category}`);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="category-card">
            <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Plant Category</div>
                    <div className="text-sm text-zinc-700 mt-0.5">
                        {canEdit ? "Sales & Super Admin can update. All edits are logged." : "Sales & Super Admin control this field."}
                    </div>
                </div>
                {plant.category && !canEdit && <CategoryBadge category={plant.category} />}
            </div>
            {canEdit ? (
                <div className="flex items-center gap-2 flex-wrap">
                    {Object.entries(CATEGORY_META).map(([key, meta]) => {
                        const Icon = meta.icon;
                        const active = plant.category === key;
                        return (
                            <button
                                key={key}
                                onClick={() => set(key)}
                                disabled={saving}
                                data-testid={`category-btn-${key}`}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-sm font-medium border transition-colors ${active ? "bg-[#0B0D11] text-white border-[#0B0D11]" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}
                            >
                                <Icon className="w-4 h-4" /> {meta.label}
                            </button>
                        );
                    })}
                </div>
            ) : (
                !plant.category && <div className="text-sm text-zinc-400 italic">Not set</div>
            )}
            {plant.category_updated_by && (
                <div className="mt-3 pt-3 border-t border-zinc-100 text-[11px] font-mono text-zinc-500 flex items-center gap-2 flex-wrap" data-testid="category-audit-line">
                    <span className="text-zinc-400">Last set by</span>
                    <span className="text-zinc-700">{plant.category_updated_by}</span>
                </div>
            )}
        </div>
    );
}
